import { ref, watch } from 'vue';

/**
 * A composable that provides utilities for managing localStorage operations.
 *
 * This utility is designed to work in both browser and server-side environments, with appropriate checks to ensure
 * operations are only performed when running in a client (browser) environment.
 *
 * @returns {Object} An object containing methods to interact with localStorage.
 */
export function useLocalStorageManager() {
  var isAppRunningAsClient = process.env.CLIENT;

  /**
   * Determines if the given value is a native JavaScript type.
   *
   * @param {*} value - The value to be checked.
   * @returns {boolean} True if the value is a native type, false otherwise.
   */
  function isNativeType(value) {
    return ['string', 'number', 'boolean'].includes(typeof value) || Array.isArray(value) || value === null || value instanceof Object && value.constructor === Object;
  }

  /**
   * Serializes a value for storage.
   *
   * @param {*} value - The value to serialize.
   * @returns {Array} An array representation of the serialized value.
   */
  function serialize(value) {
    if (isNativeType(value)) {
      return [value];
    }
    if (value instanceof Map) {
      return ['Map', Array.from(value.entries())];
    }
    if (value instanceof Set) {
      return ['Set', Array.from(value)];
    }
    return [value];
  }

  /**
   * Deserializes a value from storage.
   *
   * @param {Array} serialized - The serialized value to deserialize.
   * @returns {*} The deserialized value.
   */
  function deserialize(serialized) {
    if (serialized.length === 1) {
      return serialized[0];
    }
    var [type, value] = serialized;
    switch (type) {
      case 'Map':
        return new Map(value);
      case 'Set':
        return new Set(value);
      default:
        return value;
    }
  }

  /**
   * Sets a value in localStorage.
   *
   * @param {string} key - The key under which to store the value.
   * @param {*} value - The value to store.
   */
  function set(key, value) {
    if (!isAppRunningAsClient) {
      console.debug('Cannot set to local storage; only available in the browser');
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(serialize(value)));
    } catch (err) {
      console.error('Could not set item to local storage', err);
    }
  }

  /**
   * Retrieves a value from localStorage.
   *
   * @param {string} key - The key of the value to retrieve.
   * @returns {*} The retrieved value, or null if not found.
   */
  function get(key) {
    if (!isAppRunningAsClient) {
      console.debug('Cannot get from local storage; only available in the browser');
      return null;
    }
    try {
      var storedValue = localStorage.getItem(key);
      if (!storedValue) {
        return null;
      }
      var parsedValue = JSON.parse(storedValue);
      return deserialize(parsedValue);
    } catch (err) {
      console.error('Could not get item from local storage', err);
      return null;
    }
  }

  /**
   * Removes a value from localStorage.
   *
   * @param {string} key - The key of the value to remove.
   */
  function remove(key) {
    if (!isAppRunningAsClient) {
      console.debug('Cannot remove from local storage; only available in the browser');
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (err) {
      console.error('Could not remove item from local storage', err);
    }
  }

  /**
   * Checks if a key exists in localStorage.
   *
   * @param {string} key - The key to check.
   * @returns {boolean} True if the key exists, false otherwise.
   */
  function has(key) {
    return get(key) !== null;
  }

  /**
   * Creates a reactive Vue ref that is synced with a localStorage value.
   *
   * @param {string} key - The key under which to store the value in localStorage.
   * @param {*} defaultValue - The default value to use if the key is not in localStorage.
   * @returns {import('vue').Ref} A Vue ref that is synced with the localStorage value.
   */
  function storeRef(key, defaultValue) {
    var storedValue = isAppRunningAsClient ? get(key) : null;
    var data = ref(storedValue !== null ? storedValue : defaultValue);
    if (isAppRunningAsClient) {
      watch(data, newValue => {
        if (newValue === defaultValue && has(key)) {
          return remove(key);
        }
        set(key, newValue);
      }, {
        deep: true
      });
    }
    return data;
  }
  return {
    set,
    get,
    remove,
    has,
    storeRef
  };
}